import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Card, Space, Tag,
  Popconfirm, ColorPicker, InputNumber
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../utils/axios';
import initManager from '../../utils/initManager';

const DepartmentsSettings = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    // 检查是否已初始化，避免重复获取数据
    if (!initManager.isInitialized('departments')) {
      fetchDepartments();
    } else {
      // 使用缓存的数据
      const cachedData = initManager.getCachedData('departments');
      if (cachedData) {
        setDepartments(cachedData);
      } else {
        // 如果缓存为空，重新获取
        fetchDepartments();
      }
    }
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/departments');
      
      // 确保数据格式正确
      const departmentsData = response.data.success ? response.data.data : [];
      const validData = Array.isArray(departmentsData) ? departmentsData : [];
      
      // 按排序字段排序
      const sortedDepartments = validData.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      
      setDepartments(sortedDepartments);
      
      // 缓存数据并标记为已初始化
      initManager.setCachedData('departments', sortedDepartments);
      initManager.setInitialized('departments');
    } catch (error) {
      message.error('获取部门列表失败');
      setDepartments([]); // 确保错误时设置为空数组
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDepartment(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDepartment(record);
    // 确保颜色值正确设置到表单中
    form.setFieldsValue({
      ...record,
      color: record.color || '#007bff',
      sort_order: record.sort_order || 0
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/departments/${id}`);
      message.success('删除成功');
      fetchDepartments();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      // 确保颜色值是有效的十六进制格式
      let colorValue = values.color;
      if (colorValue && typeof colorValue === 'object' && colorValue.toHexString) {
        // 如果是从 ColorPicker 返回的对象，转换为十六进制字符串
        colorValue = colorValue.toHexString();
      } else if (colorValue && typeof colorValue === 'string') {
        // 如果是字符串，确保是有效的十六进制格式
        if (!/^#[0-9A-Fa-f]{6}$/.test(colorValue)) {
          message.error('颜色格式不正确，请选择有效的颜色');
          return;
        }
      }

      const submitData = {
        ...values,
        color: colorValue,
        sort_order: values.sort_order || 0
      };

      if (editingDepartment) {
        await api.put(`/departments/${editingDepartment.id}`, submitData);
        message.success('更新成功');
      } else {
        await api.post('/departments', submitData);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchDepartments();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '部门名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={record.color}>{text}</Tag>
      )
    },
    {
      title: '部门代码',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: '颜色',
      dataIndex: 'color',
      key: 'color',
      render: (color) => (
        <div style={{ 
          width: 20, 
          height: 20, 
          backgroundColor: color, 
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }} />
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      sorter: (a, b) => a.sort_order - b.sort_order,
      defaultSortOrder: 'ascend',
      render: (text, record) => {
        return (
          <span style={{ 
            fontWeight: 'bold', 
            color: '#1890ff',
            backgroundColor: '#f0f8ff',
            padding: '2px 8px',
            borderRadius: '4px',
            border: '1px solid #d6e4ff'
          }}>
            {text !== null && text !== undefined ? text : '-'}
          </span>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      title: '操作',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个部门吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>部门管理</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加部门
        </Button>
      </div>



      <Table
        columns={columns}
        dataSource={departments}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
          defaultPageSize: 20
        }}
        onChange={(pagination, filters, sorter) => {
          // 表格变化处理
        }}
        onRow={(record, index) => {
          return {};
        }}
      />

      <Modal
        title={editingDepartment ? '编辑部门' : '添加部门'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="部门名称"
            rules={[{ required: true, message: '请输入部门名称' }]}
          >
            <Input placeholder="请输入部门名称" />
          </Form.Item>

          <Form.Item
            name="code"
            label="部门代码"
            rules={[{ required: true, message: '请输入部门代码' }]}
            extra="部门代码用于系统内部识别，建议使用英文缩写"
          >
            <Input placeholder="例如：HR、IT、FIN" />
          </Form.Item>

          <Form.Item
            name="color"
            label="部门颜色"
            initialValue="#007bff"
          >
            <ColorPicker 
              format="hex"
              showText
              presets={[
                {
                  label: '推荐颜色',
                  colors: [
                    '#007bff',
                    '#28a745',
                    '#dc3545',
                    '#ffc107',
                    '#17a2b8',
                    '#6f42c1',
                    '#fd7e14',
                    '#e83e8c',
                    '#20c997',
                    '#6c757d'
                  ]
                }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="部门描述"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="请输入部门描述..."
            />
          </Form.Item>

          <Form.Item
            name="sort_order"
            label="排序"
            initialValue={0}
            extra="数字越小，优先级越高，用于控制部门在列表中的显示顺序"
          >
            <InputNumber 
              min={0} 
              max={9999}
              placeholder="请输入排序号" 
              style={{ width: '100%' }} 
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingDepartment ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DepartmentsSettings; 